import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Store, ChevronDown, ChevronUp } from "lucide-react";

const ageColor = (d) => (d <= 30 ? "text-emerald-400" : d <= 60 ? "text-amber-400" : d <= 90 ? "text-orange-500" : "text-rose-500");

export const SubdealerInventory = ({ fq }) => {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);
  useEffect(() => { api.get(`/analysis/subdealers${fq ? `?filters=${fq}` : ""}`).then((r) => setData(r.data)).catch(() => {}); }, [fq]);
  if (!data) return <div className="h-48 bg-[#1E293B] border border-[#334155] rounded-lg animate-pulse" />;

  return (
    <div data-testid="subdealer-inventory-panel" className="bg-[#1E293B] border border-blue-600/40 rounded-lg p-5">
      <h3 className="text-lg font-semibold mb-1 flex items-center gap-2" style={{ fontFamily: "Outfit, sans-serif" }}>
        <Store className="h-5 w-5 text-blue-400" /> Subdealer Inventory (B2B)
      </h3>
      <p className="text-xs text-slate-400 mb-4">Delivered to ASC subdealers but RTO not done yet — this is their unsold stock. {data.reduce((a, s) => a + s.count, 0)} vehicles across {data.length} subdealers.</p>
      <div className="space-y-3">
        {data.map((s, i) => (
          <div key={s.party} data-testid={`subdealer-card-${i}`} className="rounded-lg border border-[#334155] bg-[#111827] p-4 cursor-pointer transition-all duration-200 hover:border-blue-500/50"
            onClick={() => setOpen(open === i ? null : i)}>
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                <p className="font-semibold text-slate-100 truncate">{s.party}</p>
                <p className="text-xs text-slate-400 mt-0.5">
                  {s.models.map((m) => `${m.model} ×${m.count}`).join(" · ")}
                </p>
              </div>
              <div className="flex items-center gap-4 shrink-0">
                <div className="text-right">
                  <p className="text-xl font-extrabold font-mono text-blue-400">{s.count}</p>
                  <p className="text-[10px] uppercase tracking-wider text-slate-500">in stock</p>
                </div>
                <div className="text-right">
                  <p className={`text-xl font-extrabold font-mono ${ageColor(s.avg_age)}`}>{s.avg_age}d</p>
                  <p className="text-[10px] uppercase tracking-wider text-slate-500">avg age</p>
                </div>
                <Badge variant="outline" className={`${ageColor(s.oldest)} border-slate-600 font-mono text-[10px]`}>oldest {s.oldest}d</Badge>
                {open === i ? <ChevronUp className="h-4 w-4 text-slate-500" /> : <ChevronDown className="h-4 w-4 text-slate-500" />}
              </div>
            </div>
            {open === i && (
              <div className="mt-3 pt-3 border-t border-slate-800 max-h-64 overflow-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500">
                      <th className="pb-1 pr-2">Chassis</th><th className="pb-1 pr-2">Model</th><th className="pb-1 pr-2">Delivered</th><th className="pb-1 text-right">Age</th>
                    </tr>
                  </thead>
                  <tbody>
                    {s.chassis.map((c) => (
                      <tr key={c.chassis_no} className="border-t border-slate-800/50">
                        <td className="py-1.5 pr-2 font-mono text-slate-300">{c.chassis_no}</td>
                        <td className="py-1.5 pr-2 text-slate-400">{c.model_name} {c.model_code && <span className="text-slate-500 font-mono">({c.model_code})</span>}</td>
                        <td className="py-1.5 pr-2 font-mono text-slate-500">{c.sale_date}</td>
                        <td className={`py-1.5 text-right font-mono ${ageColor(c.age_days)}`}>{c.age_days}d</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ))}
        {data.length === 0 && <p className="text-sm text-slate-500">No subdealer stock pending RTO.</p>}
      </div>
    </div>
  );
};
